import { h, RefObject } from 'preact';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { cAF, rAF } from '../../raf';
import { CSSAttribute } from '../../types';
import { PreviewContext } from '../contexts/previewContext';
import { Keywords } from '../hooks/useTextKeywords';
import { teads_tag } from '../theme';

export interface LineClampProps {
  lines: number;
  /**
   * @deprecated Use "fontSize" instead
   */
  lineHeight?: number;
  fontSize?: number;
  text: string;
  dots: boolean;
  style?: CSSAttribute;
  styleWord?: (str: string) => CSSAttribute;
  prefix?: any;
  keywords?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  className?: string;
}

function textNodes(parent: HTMLElement): Node[] {
  const walker = document.createTreeWalker(parent, NodeFilter.SHOW_TEXT, function filter(node: Node): number {
    return NodeFilter.FILTER_ACCEPT;
  } as any);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

function replaceElement(parent: HTMLElement, replace: Node, children: HTMLElement[]) {
  if (!parent) {
    console.error('replaceElement with no parent');
    return;
  }
  const frag = document.createDocumentFragment();
  children.forEach((child) => frag.appendChild(child));
  parent.insertBefore(frag, replace);
  if (replace) parent.removeChild(replace);
}

const partsSplitWithoutKeywordsRegex = /(\s+)/;
const partsSplitWithKeywordsRegex = new RegExp(`((?:\\/Keyword \\[(?:${Keywords.join('|')})])|(?:\\s+))`);

const keywordRegex = new RegExp(`^\\/Keyword (\\[(?:${Keywords.join('|')})])$`);
const keywordStyle = { color: teads_tag };

function insertText(
  element: HTMLElement,
  text: string,
  prefixElement: HTMLElement | undefined,
  styleWord: { (word: string): CSSAttribute } | null,
  keywords: boolean = false
) {
  const createSpan = (text: string, extraStyle?: CSSAttribute) => {
    const elem = document.createElement('span');
    Object.assign(elem.style, styleWord && styleWord(text), extraStyle);
    elem.appendChild(document.createTextNode(text));
    return elem;
  };

  element.innerHTML =
    text
      ?.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
      .replace(/\n/g, '<br>')
      .replace(/\u00a0/g, '\u0020') ?? ''; // NBSP -> Space

  const partsSplitRegex = keywords ? partsSplitWithKeywordsRegex : partsSplitWithoutKeywordsRegex;
  const nodes = textNodes(element);
  for (const node of nodes) {
    const parent = node.parentNode as HTMLElement;
    const text = node.textContent || '';
    const parts = text
      .split(partsSplitRegex)
      .filter((_) => _.length !== 0)
      .reduce<HTMLSpanElement[]>((result, part) => {
        if (keywords) {
          const match = part.match(keywordRegex);

          if (match) {
            result.push(createSpan(match[1], keywordStyle));
          } else {
            result.push(createSpan(part));
          }
        } else {
          result.push(createSpan(part));
        }

        return result;
      }, []);
    replaceElement(parent, node, parts);
  }
  prefixElement && element.insertBefore(prefixElement, element.firstChild);
}

function fontSizeToLineHeight(fontSize: number) {
  return Math.ceil(fontSize * 1.1666);
}

interface LineClampStyle {
  lines: number;
  /**
   * @deprecated Use "fontSize" instead
   */
  lineHeight?: number;
  fontSize?: number;
  dots: boolean;
  style?: CSSAttribute;
}

enum iOSFixStatus {
  RemoveDisplay,
  ReaddDisplay,
  Done,
}

function getLineClampStyle(
  lines: number,
  lineHeight: number | undefined,
  fontSize: number | undefined,
  dots: boolean,
  style: object | undefined
) {
  const hasFontSize = typeof fontSize !== 'undefined';
  const hasLineHeight = typeof lineHeight !== 'undefined';

  if (!hasFontSize && !hasLineHeight) {
    throw new Error('You must specify the lineHeight or the fontSize.');
  }

  lineHeight = hasFontSize ? fontSizeToLineHeight(fontSize) : lineHeight!;

  return {
    ...style,
    overflow: 'hidden',
    'line-height': lineHeight,
    'max-height': `${Math.ceil(lines * lineHeight)}px`,
    ...(dots && {
      display: '-webkit-box',
      '-webkit-line-clamp': lines,
      '-webkit-box-orient': 'vertical',
    }),
  };
}

function useLineClampStyle({ lines, lineHeight, fontSize, dots, style }: LineClampStyle) {
  const [base, setBase] = useState<any>(getLineClampStyle(lines, lineHeight, fontSize, dots, style));
  const [result, setResult] = useState<any>(base);
  const isPreview = useContext(PreviewContext);

  const fixStatus = useRef<iOSFixStatus>(iOSFixStatus.Done);

  useEffect(() => {
    const base = getLineClampStyle(lines, lineHeight, fontSize, dots, style);

    setBase(base);
    setResult(base);
    if (!isPreview && dots) fixStatus.current = iOSFixStatus.RemoveDisplay;
  }, [lines, lineHeight, fontSize, dots, style, isPreview]);

  useEffect(() => {
    if (fixStatus.current == iOSFixStatus.Done) return;

    if (fixStatus.current === iOSFixStatus.RemoveDisplay) {
      const { display, ...other } = base;
      setResult(other);
      fixStatus.current = iOSFixStatus.ReaddDisplay;
    } else if (fixStatus.current === iOSFixStatus.ReaddDisplay) {
      let id = -1;

      id = rAF(() => {
        id = rAF(() => {
          fixStatus.current = iOSFixStatus.Done;
          setResult(base);
        });
      });

      return () => cAF(id);
    }
  }, [base, fixStatus.current]);

  return result;
}

export function LineClamp(props: LineClampProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback(
    (e: HTMLDivElement | null) => {
      ref.current = e;
      if (props.containerRef) props.containerRef.current = e;
    },
    [props.containerRef]
  );
  useLayoutEffect(() => {
    ref.current && insertText(ref.current, props.text, props.prefix, props.styleWord || null, props.keywords);
  }, [props.text, props.prefix, props.styleWord]);
  const style = useLineClampStyle(props);
  return <div style={style} className={props.className} ref={setRef} />;
}
